'use client';
import React from 'react';
import { useNotifications, Notification } from '@/context/NotificationsContext';
import { Bell, ArrowUpRight, Check, X, User } from 'lucide-react';
import { respondRequest } from '@/hooks/use-follow'
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import SwipeToDeleteItem from './SwipeToDeleteItem';
import instance from '@/lib/axiosInstance';
import { useFormatDateToDisplay } from '@/lib/utils';

export default function Notifications() {
    const formatDate = useFormatDateToDisplay();
    const {
        isOpen,
        closeNotifications,
        setNotifications,
        notifications,
        markAllAsRead,
        markAsRead,
    } = useNotifications();
    const { t } = useTranslation();

    const handleNotificationClick = (notification: Notification) => {
        markAsRead(notification.id);
    };

    const handleButtonClick = (notification: Notification) => {
        if (notification.data.action_url) {
            closeNotifications();

            // Small delay to ensure the drawer closes smoothly
            setTimeout(() => {
                window.location.href = notification.data.action_url!;
            }, 300);
        }
    };

    const handleAccept = async (notification: Notification) => {
        if (notification.data.follow_request_id !== undefined) {
            await respondRequest(
                notification.data.follow_request_id,
                'accepted'
            );
            setNotifications((prev) =>
                prev.filter((n) => n.id !== notification.id)
            );
        }
    };

    const handleReject = async (notification: Notification) => {
        if (notification.data.follow_request_id !== undefined) {
            await respondRequest(
                notification.data.follow_request_id,
                'rejected'
            );
            setNotifications((prev) =>
                prev.filter((n) => n.id !== notification.id)
            );
        }
    };

    const unreadLikes = notifications.filter(n =>
        n.data.type === 'like' && n.read_at == null
    );

    const handleDeleteNotification = (notificationId: string) => {
        setNotifications((prev) =>
            prev.filter((n) => n.id !== notificationId)
        );

        const response = instance.delete(`/notifications/${notificationId}`);
        response.catch((error) => {
            console.error('Failed to delete notification:', error);
        }

        );
    };

    const renderLikeGroupNotification = () => {
        if (unreadLikes.length > 2) {
            const [first, second, ...rest] = unreadLikes;
            const remaining = rest.length;

            const msg = t('notifications.like.multipleMore', {
                user1: first.data.message_params.likerName,
                user2: second.data.message_params.likerName,
                remaining,
                defaultValue: '{{user1}}, {{user2}} and {{remaining}} others liked your posts'
            });

            return (
                <SwipeToDeleteItem
                    className="relative rounded-lg sm:rounded-xl border border-primary/10 transition-all duration-200 hover:bg-accent hover:shadow-md group bg-gradient-to-r from-primary/3 to-primary/1 w-full hover:border-primary/20 active:scale-[0.98] touch-manipulation"
                    onDelete={() => {
                        // Delete all grouped like notifications
                        unreadLikes.forEach(n => handleDeleteNotification(n.id));
                    }}
                    onClick={() => {
                        // Mark all those "like" notifications as read
                        unreadLikes.forEach(n => markAsRead(n.id));
                    }}
                >
                    <span className="absolute right-2 sm:right-3 top-2 sm:top-3 h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-primary shadow-md ring-1 ring-background animate-pulse"></span>
                    <div className="flex items-start gap-3 sm:gap-4">
                        <div className="bg-gradient-to-br from-pink-100 to-pink-200 dark:from-pink-900/40 dark:to-pink-900/20 rounded-full p-2 sm:p-2.5 flex-shrink-0 shadow-sm">
                            <Bell className="h-4 w-4 sm:h-5 sm:w-5 text-pink-600 dark:text-pink-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm sm:text-base font-medium text-foreground leading-relaxed">{msg}</p>
                            <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 sm:mt-2">
                                {formatDate(unreadLikes[0].created_at)}
                            </p>
                        </div>
                    </div>
                </SwipeToDeleteItem>
            );
        }

        // If exactly 2 unread likes, show "A and B liked..." 
        if (unreadLikes.length === 2) {
            const [a, b] = unreadLikes;
            const msg = t('notifications.like.multiple2', {
                user1: a.data.message_params.likerName,
                user2: b.data.message_params.likerName,
                defaultValue: '{{user1}} and {{user2}} liked your posts'
            });

            return (
                <SwipeToDeleteItem
                    className="relative rounded-lg sm:rounded-xl border border-primary/10 transition-all duration-200 hover:bg-accent hover:shadow-md group bg-gradient-to-r from-primary/3 to-primary/1 w-full hover:border-primary/20 active:scale-[0.98] touch-manipulation"
                    onDelete={() => {
                        // Delete all grouped like notifications
                        unreadLikes.forEach(n => handleDeleteNotification(n.id));
                    }}
                    onClick={() => {
                        // Mark all those "like" notifications as read
                        unreadLikes.forEach(n => markAsRead(n.id));
                    }}
                >
                    <span className="absolute right-2 sm:right-3 top-2 sm:top-3 h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-primary shadow-md ring-1 ring-background animate-pulse"></span>
                    <div className="flex items-start gap-3 sm:gap-4">
                        <div className="bg-gradient-to-br from-pink-100 to-pink-200 dark:from-pink-900/40 dark:to-pink-900/20 rounded-full p-2 sm:p-2.5 flex-shrink-0 shadow-sm">
                            <Bell className="h-4 w-4 sm:h-5 sm:w-5 text-pink-600 dark:text-pink-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm sm:text-base font-medium text-foreground leading-relaxed">{msg}</p>
                            <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 sm:mt-2">
                                {formatDate(unreadLikes[0].created_at)}
                            </p>
                        </div>
                    </div>
                </SwipeToDeleteItem>
            );
        }

        return null;
    };

    return (
        <Drawer open={isOpen} onOpenChange={closeNotifications}>
            <DrawerContent className="w-full sm:w-[480px] md:w-[520px] lg:w-[600px] xl:w-[640px] 2xl:w-[700px]">
                <DrawerHeader className="pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <DrawerTitle className="text-lg sm:text-xl md:text-2xl font-semibold">
                                {t('notifications.title', 'Notifications')}
                            </DrawerTitle>
                            <DrawerDescription className="text-xs sm:text-sm text-muted-foreground mt-1">
                                {t('notifications.description', 'Stay updated with your latest activity')}
                            </DrawerDescription>
                        </div>
                        {notifications.length > 0 && (
                            <div className="ml-3 text-xs sm:text-sm text-muted-foreground bg-muted/30 px-2 py-1 rounded-full border">
                                {notifications.filter(n => !n.read_at).length > 0 ? (
                                    <span className="font-medium text-primary">
                                        {notifications.filter(n => !n.read_at).length} new
                                    </span>
                                ) : (
                                    <span>All read</span>
                                )}
                            </div>
                        )}
                    </div>
                </DrawerHeader>

                <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted/30">
                    {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20 text-center">
                            <div className="bg-gradient-to-br from-muted/30 to-muted/50 rounded-full p-6 sm:p-8 mb-4 sm:mb-6 shadow-sm">
                                <Bell className="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 text-muted-foreground" />
                            </div>
                            <h3 className="font-medium text-base sm:text-lg mb-2">No notifications yet</h3>
                            <p className="text-xs sm:text-sm text-muted-foreground max-w-xs sm:max-w-sm leading-relaxed">
                                {t('notifications.empty', 'You\'ll see notifications about flights, follows, and other activity here.')}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2 sm:space-y-3">
                            {/* Render grouped like notifications if applicable */}
                            {unreadLikes.length >= 2 && renderLikeGroupNotification()}

                            {/* Render individual notifications, but filter out likes if they're grouped */}
                            {notifications
                                .filter(notification =>
                                    !(unreadLikes.length >= 2 && notification.data.type === 'like' && !notification.read_at)
                                )
                                .map((notification) => {
                                    const isFollowRequest = notification.data.follow_request_id !== undefined;
                                    const params = { ...notification.data.message_params };

                                    if (params.source === 'system') {
                                        params.source = t('notifications.source.system');
                                    }

                                    return (
                                        <SwipeToDeleteItem
                                            key={notification.id}
                                            className={`relative rounded-lg sm:rounded-xl border transition-all duration-200 w-full hover:shadow-md active:scale-[0.98] touch-manipulation
                                                ${!notification.read_at 
                                                    ? 'bg-gradient-to-r from-primary/5 to-primary/2 border-primary/15 shadow-sm hover:border-primary/25' 
                                                    : 'bg-background hover:bg-accent/30 border-border/50 hover:border-border'}
                                                ${notification.data.action_url && !isFollowRequest ? 'hover:bg-accent/50 group cursor-pointer' : ''}
                                            `}
                                            onDelete={() => handleDeleteNotification(notification.id)}
                                            onClick={isFollowRequest ? undefined : () => handleNotificationClick(notification)}
                                        >
                                            {!notification.read_at && (
                                                <span className="absolute right-2 sm:right-3 top-2 sm:top-3 h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-primary shadow-md ring-1 ring-background animate-pulse"></span>
                                            )}

                                            <div className="flex items-start gap-3 sm:gap-4">
                                                <div className={`rounded-full p-2 sm:p-2.5 flex-shrink-0 shadow-sm transition-all duration-200 ${
                                                    isFollowRequest 
                                                        ? 'bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-900/20' 
                                                        : !notification.read_at
                                                        ? 'bg-gradient-to-br from-primary/15 to-primary/10'
                                                        : 'bg-gradient-to-br from-muted to-muted/50'
                                                }`}>
                                                    {isFollowRequest ? (
                                                        <User className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
                                                    ) : (
                                                        <Bell className={`h-4 w-4 sm:h-5 sm:w-5 transition-colors ${!notification.read_at ? 'text-primary' : 'text-muted-foreground'}`} />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-3 mb-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm sm:text-base font-medium text-foreground leading-relaxed">
                                                                {notification.data.title || String(t(`notifications.${notification.data.notification_title}`, params))}
                                                            </p>
                                                            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-1">
                                                                {String(t(`notifications.${notification.data.message_key}`, params))}
                                                            </p>
                                                        </div>
                                                        <div className="flex-shrink-0 self-start">
                                                            <p className="text-xs text-muted-foreground whitespace-nowrap bg-muted/30 px-2 py-1 rounded-md border">
                                                                {formatDate(notification.created_at)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {(isFollowRequest || notification.data.action_url) && (
                                                <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2 sm:gap-3 mt-3 pl-12 sm:pl-14 md:pl-16">
                                                    {isFollowRequest ? (
                                                        <>
                                                            <Button
                                                                variant="default"
                                                                size="sm"
                                                                className="text-xs sm:text-sm cursor-pointer h-8 sm:h-9 px-4 sm:px-5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 dark:from-green-700 dark:to-green-800 dark:hover:from-green-800 dark:hover:to-green-900 shadow-md hover:shadow-lg transition-all duration-200 font-medium rounded-lg active:scale-95 touch-manipulation flex-1 xs:flex-none"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAccept(notification);
                                                                }}
                                                            >
                                                                <Check className="mr-1.5 h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                                                <span className="truncate">{t('notifications.follow_request.accept', 'Accept')}</span>
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="text-xs sm:text-sm cursor-pointer h-8 sm:h-9 px-4 sm:px-5 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 dark:hover:from-red-950/30 dark:hover:to-red-950/50 hover:border-red-300 dark:hover:border-red-700 transition-all duration-200 font-medium rounded-lg active:scale-95 touch-manipulation flex-1 xs:flex-none"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleReject(notification);
                                                                }}
                                                            >
                                                                <X className="mr-1.5 h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                                                <span className="truncate">{t('notifications.follow_request.reject', 'Reject')}</span>
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-xs sm:text-sm cursor-pointer h-8 sm:h-9 px-4 sm:px-5 bg-gradient-to-r from-primary/8 to-primary/12 hover:from-primary/12 hover:to-primary/20 dark:from-primary/10 dark:to-primary/15 dark:hover:from-primary/15 dark:hover:to-primary/25 text-primary font-medium transition-all duration-200 rounded-lg active:scale-95 touch-manipulation group"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleButtonClick(notification);
                                                            }}
                                                        >
                                                            <span className="truncate">{t('notifications.view', 'View')}</span>
                                                            <ArrowUpRight className="ml-1.5 h-3 w-3 sm:h-3.5 sm:w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </SwipeToDeleteItem>
                                    );
                                })}
                        </div>
                    )}
                </div>

                <DrawerFooter className="pt-4 pb-4 sm:pb-6 px-4 sm:px-6 border-t border-border/50 bg-background/95 backdrop-blur-sm">
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full">
                        {notifications.filter(n => !n.read_at).length > 0 && (
                            <Button 
                                variant="outline" 
                                className="cursor-pointer bg-gradient-to-r from-primary/8 to-primary/12 hover:from-primary/12 hover:to-primary/20 border-primary/20 text-primary font-medium transition-all duration-200 flex-1 sm:flex-none h-10 sm:h-11 active:scale-95 touch-manipulation shadow-sm hover:shadow-md"
                                onClick={markAllAsRead}
                            >
                                <Check className="mr-2 h-4 w-4" />
                                <span className="truncate">
                                    {t('notifications.markAllAsRead', 'Mark all as read')}
                                </span>
                            </Button>
                        )}
                        <DrawerClose asChild>
                            <Button 
                                variant="outline"
                                className="font-medium transition-all duration-200 flex-1 sm:flex-none h-10 sm:h-11 hover:bg-muted/50 active:scale-95 touch-manipulation shadow-sm hover:shadow-md"
                            >
                                {t('notifications.close', 'Close')}
                            </Button>
                        </DrawerClose>
                    </div>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
}